import React from 'react';
import { motion } from 'framer-motion';
import Process4 from './Process4';

const SingleServiceModern = ({ service }) => {
  if (!service) return null;

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Hero Section */}
      <section className="relative md:pt-32 pt-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <span className="inline-block px-6 py-2 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 text-primary text-sm font-medium mb-6">
              {service.subtitle}
            </span>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
              {service.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 max-w-7xl mx-auto">
              {service.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="relative pb-20 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent rounded-3xl transform -rotate-2" />
              <div className="relative p-8 rounded-3xl bg-white/5 backdrop-blur-md border border-white/10">
                <h2 className="text-4xl font-bold text-white mb-6">Why Choose Our {service.overviewTitle}?</h2>
                <p className="text-gray-400 mb-6">
                  {service.overview}
                </p>
                <ul className="space-y-4">
                  <li className="flex items-center text-gray-300">
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center mr-3">✓</span>
                    Increased Visibility in Search Results
                  </li>
                  <li className="flex items-center text-gray-300">
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center mr-3">✓</span>
                    Higher Conversion Rates
                  </li>
                  <li className="flex items-center text-gray-300">
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center mr-3">✓</span>
                    Improved Customer Experience
                  </li>
                </ul>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-video rounded-3xl bg-white/5 backdrop-blur-md border border-white/10 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent opacity-50" />
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover opacity-80"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-20 bg-white/[0.02]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">{service.optimization}</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {service.optimizationDescription}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {service.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative p-8 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 transition-colors duration-300">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                    <span className="text-2xl">{['⚡', '🎯', '🔍', '📊'][index % 4]}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section with Stats */}
      <section className="relative py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              <h2 className="text-4xl font-bold text-white">{service.results}</h2>
              <p className="text-gray-400">
                {service.resultsDescription}
              </p>
              <div className="grid grid-cols-2 gap-6">
                {service.stat.map((stat, index) => (
                  <div
                    key={index}
                    className="p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10"
                  >
                    <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="grid grid-cols-1 gap-6"
            >
              {service.benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 flex items-center gap-4"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">{['🎯', '📈', '🚀', '💡'][index % 4]}</span>
                  </div>
                  <p className="text-white/90 font-medium">
                    {benefit}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Process4 />
    </div>
  );
};

export default SingleServiceModern;
